// PlatformDetector.js
import { useEffect } from "react"
import Value from "aldoo-ra/Value"

export const getPlatform = () => {
  const userAgent = window.navigator.userAgent.toLowerCase()

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios"
  }
  if (/android/.test(userAgent)) {
    return "android"
  }
  if (/mac/.test(userAgent) && !/iphone|ipad|ipod/.test(userAgent)) {
    return "mac"
  }
  if (/win/.test(userAgent)) {
    return "windows"
  }
  if (/linux/.test(userAgent)) {
    return "linux"
  }
  return "unknown"
}

// Known in-app browsers (webviews). WebRTC getUserMedia, downloads, and
// other platform APIs are often blocked or unreliable inside these.
const IN_APP_BROWSERS = [
  { id: "facebook", test: /\bFBAN\b|\bFBAV\b|\bFB_IAB\b|FBIOS|FB4A/i },
  { id: "messenger", test: /\bFBAN\/Messenger|Messenger\b/i },
  { id: "instagram", test: /Instagram/i },
  { id: "viber", test: /Viber/i },
  { id: "line", test: /\bLine\//i },
  { id: "wechat", test: /MicroMessenger/i },
  { id: "tiktok", test: /BytedanceWebview|musical_ly|Bytedance|TikTok/i },
  { id: "snapchat", test: /Snapchat/i },
  { id: "twitter", test: /Twitter/i },
  { id: "telegram", test: /Telegram/i },
  { id: "linkedin", test: /LinkedInApp/i },
  { id: "pinterest", test: /Pinterest/i },
  { id: "kakao", test: /KAKAOTALK/i },
]

// Returns the in-app browser id (e.g. "viber") or null for a real browser.
export const getInAppBrowser = (ua = window.navigator.userAgent) => {
  const match = IN_APP_BROWSERS.find(({ test }) => test.test(ua))
  if (match) return match.id

  // Heuristic fallback: iOS WKWebView that is not Safari and not a
  // standalone PWA. Real Safari UA contains "Safari" + "Version/".
  const isIOS = /iphone|ipad|ipod/i.test(ua)
  if (isIOS) {
    const isSafari = /Safari/i.test(ua) && /Version\//i.test(ua)
    const isChromeIOS = /CriOS/i.test(ua)
    const isFirefoxIOS = /FxiOS/i.test(ua)
    const isStandalone = window.navigator.standalone === true
    if (!isSafari && !isChromeIOS && !isFirefoxIOS && !isStandalone) {
      return "ios-webview"
    }
  }
  return null
}

// Boolean convenience wrapper.
export const isInAppBrowser = (ua) => getInAppBrowser(ua) !== null

// Hook version for use in other components
export const usePlatformDetector = () => {
  const [detectedPlatform, setDetectedPlatform] = Value({
    key: "detected_platform",
    defaultValue: null,
    persistKey: "detected_platform",
  })

  useEffect(() => {
    const detected = getPlatform()
    setDetectedPlatform(detected)
  }, [])

  return detectedPlatform
}

// Hook: returns the in-app browser id (or null). Evaluated client-side.
export const useInAppBrowser = () => {
  const [browser, setBrowser] = Value({
    key: "in_app_browser",
    defaultValue: undefined,
    persistKey: "in_app_browser",
  })

  useEffect(() => {
    setBrowser(getInAppBrowser())
  }, [])

  return browser
}

const PlatformDetector = () => {
  usePlatformDetector()
  return null
}

export default PlatformDetector
